import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { CommonIcons } from 'common-src/assets/Icons';
import { getActivePatientMedications, getFrequencyById } from 'common-src/models/PatientMedication';

import { InfoItem, SeparatorLine } from 'src/components/elements';
import useCustomSelector from 'src/hooks/useCustomSelector';

import styles from './Medications.module.scss';

const ATHENA_SOURCE = 6;
const ACTIVE_STATUS = 1;

const getMedicationIdentity = (medication) =>
  [medication.name, medication.dosage, medication.form]
    .map((value) => (value || '').toLowerCase().split(/\s+/).filter(Boolean).join(' '))
    .join('|');

const getMedicationRecency = (medication) =>
  [medication.filledDate, medication.startDate, medication.updatedAt].map((date) =>
    date ? new Date(date).getTime() : 0,
  );

const isMoreRecent = (a, b) => {
  const [recencyA, recencyB] = [getMedicationRecency(a), getMedicationRecency(b)];
  const index = recencyA.findIndex((value, i) => value !== recencyB[i]);
  return index !== -1 && recencyA[index] > recencyB[index];
};

const getAthenaMedications = (medications) => {
  const latestByIdentity = {};

  medications
    .filter(
      (medication) => medication.source === ATHENA_SOURCE && medication.status === ACTIVE_STATUS,
    )
    .forEach((medication) => {
      const identity = getMedicationIdentity(medication);
      const current = latestByIdentity[identity];
      if (!current || isMoreRecent(medication, current)) latestByIdentity[identity] = medication;
    });

  return _.sortBy(Object.entries(latestByIdentity), ([identity]) => identity).map(
    ([, medication]) => medication,
  );
};

const MedicationsModule = ({ patientId, medications, setMedications, meta }) => {
  const patientMedications = useCustomSelector((state) =>
    getActivePatientMedications(state, patientId),
  );
  const activeMedications = useMemo(
    () => (meta?.athenaOnly ? getAthenaMedications(patientMedications) : patientMedications),
    [patientMedications, meta?.athenaOnly],
  );

  const [isExpanded, setIsExpanded] = useState(!!meta?.isExpanded);

  useEffect(() => {
    if (_.isEmpty(activeMedications)) return;

    const transformedMedications = activeMedications.map((medication) => ({
      medication: medication.med?.fullName?.split(', ', 3)?.join(', ') || '-',
      createdAt: medication.createdAt ? moment(medication.createdAt).format('MM/DD/YYYY') : '-',
      filledDate: medication.filledDate ? moment(medication.filledDate).format('MM/DD/YYYY') : '-',
      frequency: getFrequencyById(medication.frequency)?.label || '-',
      instructions: medication.instructions?.replace(/\s+/g, ' ') || '-',
    }));
    setMedications(transformedMedications);
  }, [activeMedications]);

  const renderMedications = () => {
    if (_.isEmpty(medications)) {
      return <span className={styles.placeholder}>No medications available</span>;
    }

    return medications.map((medication, index) => (
      <div key={index}>
        <div className={styles.grid}>
          <InfoItem
            textId="medication"
            title="Medication"
            content={medication.medication}
            isSmall
          />
          <InfoItem
            title="Date Written"
            content={medication.createdAt}
            textId="date-written"
            isSmall
          />
          <InfoItem
            title="Date Filled"
            content={medication.filledDate}
            textId="date-filled"
            isSmall
          />
          <InfoItem title="Frequency" content={medication.frequency} textId="frequency" isSmall />
          <InfoItem
            classNames={[styles.width100]}
            title="Instructions"
            content={medication.instructions}
            textId="instructions"
            multiline
            isSmall
          />
        </div>
        <SeparatorLine classNames={['m-b-20', 'm-t-20']} />
      </div>
    ));
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.header}
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setIsExpanded((prev) => !prev);
        }}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
        }}
      >
        <span className={[styles.title, 'font-w-700', 'font-s-18'].join(' ')}>Medications</span>
        <img
          className={[styles.toggleButton, isExpanded ? styles.expanded : styles.collapsed].join(
            ' ',
          )}
          src={CommonIcons.downIcon}
          alt="expand-collapse-btn"
          role="presentation"
          aria-hidden="true"
        />
      </div>

      {isExpanded && <div className={styles.wrapper}>{renderMedications()}</div>}
    </div>
  );
};

MedicationsModule.propTypes = {
  patientId: PropTypes.number,
  medications: PropTypes.array,
  setMedications: PropTypes.func,
  meta: PropTypes.object,
};

export default MedicationsModule;
